import { useUser } from "@clerk/clerk-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { api } from "@/services/api";
import type { UserProgramme } from "@/types";

interface EnrollmentData {
  id: string;
  user_id: string;
  programme_id: string;
  enrolled_at: string;
  status: string;
  current_week: number;
  total_score: number | null;
  submissions_count: number;
  reviews_count: number;
  // Set once a Free Participant dismisses the unlock screen (KC-498)
  unlock_screen_dismissed_at?: string | null;
  programme: {
    id: string;
    name: string;
    status: string;
    start_date: string;
    end_date: string;
    total_weeks: number;
    is_freemium?: boolean;
  };
}

interface EnrollmentContextType {
  enrollment: EnrollmentData | null;
  allEnrollments: UserProgramme[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  switchProgramme: (programmeId: string) => Promise<void>;
}

const EnrollmentContext = createContext<EnrollmentContextType | null>(null);

export function EnrollmentProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded: isClerkLoaded } = useUser();
  const [enrollment, setEnrollment] = useState<EnrollmentData | null>(null);
  const [allEnrollments, setAllEnrollments] = useState<UserProgramme[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // `/users/me/active-enrollment` auto-redeems pending enrollment codes, so
  // firing it concurrently used to race that write and mint duplicate
  // enrollments. Both listeners below can fire for one tab switch, and Clerk
  // hands us a new `user` object on every token refresh, so overlapping calls
  // were routine. One in flight at a time (KC-469).
  const inFlight = useRef<Promise<void> | null>(null);
  const userId = user?.id ?? null;

  // The API client's JWT token getter is installed by <ApiAuthBridge>, mounted
  // above the QueryClientProvider so queries are authenticated from first paint.

  const fetchEnrollment = useCallback(
    async ({ background = false }: { background?: boolean } = {}) => {
      // Wait for Clerk to finish loading
      if (!isClerkLoaded) {
        return;
      }

      // User not signed in
      if (!userId) {
        setIsLoading(false);
        setEnrollment(null);
        setAllEnrollments([]);
        return;
      }

      // Join the request already running instead of starting a second one.
      if (inFlight.current) {
        return inFlight.current;
      }

      const run = async () => {
        try {
          // Background refreshes keep the current data on screen instead of
          // flashing the sidebar loader.
          if (!background) {
            setIsLoading(true);
          }
          setError(null);

          // Fetch current active enrollment (respects preferred programme)
          const activeEnrollment = await api.getMyActiveEnrollment();
          setEnrollment(activeEnrollment);

          // Fetch all active enrollments for programme switcher
          const enrollments = await api.getMyEnrollments("active");
          setAllEnrollments(enrollments);
        } catch (err) {
          console.error("Failed to fetch enrollment:", err);
          if (background) {
            return; // Keep showing the last good data
          }
          setError(err instanceof Error ? err : new Error("Failed to fetch enrollment"));
          setEnrollment(null);
          setAllEnrollments([]);
        } finally {
          setIsLoading(false);
        }
      };

      const pending = run().finally(() => {
        inFlight.current = null;
      });
      inFlight.current = pending;
      return pending;
    },
    [isClerkLoaded, userId]
  );

  const refetch = useCallback(() => fetchEnrollment({ background: true }), [fetchEnrollment]);

  const switchProgramme = useCallback(
    async (programmeId: string) => {
      try {
        await api.setMyPreferredProgramme(programmeId);
        await fetchEnrollment(); // Reload with new preferred programme
      } catch (err) {
        console.error("Failed to switch programme:", err);
        throw err;
      }
    },
    [fetchEnrollment]
  );

  useEffect(() => {
    fetchEnrollment();
  }, [fetchEnrollment]);

  // Stats like total_score change server-side (e.g. an admin publishes a
  // review), so refresh whenever the user returns to the tab (KC-338).
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refetch]);

  return (
    <EnrollmentContext.Provider
      value={{
        enrollment,
        allEnrollments,
        isLoading,
        error,
        refetch,
        switchProgramme,
      }}
    >
      {children}
    </EnrollmentContext.Provider>
  );
}

export function useEnrollment() {
  const context = useContext(EnrollmentContext);
  if (!context) {
    throw new Error("useEnrollment must be used within EnrollmentProvider");
  }
  return context;
}
